import { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { datadogRum } from '@wehunt/core-web'
import { ReplayBundleView } from './ReplayBundleView'
import { ReplayStatusScreen } from './ReplayStatusScreen'
import { replayDtoToBundle } from './replayDtoToBundle'
import { useSharedHuntReplay } from './useSharedHuntReplay'

/**
 * The shared replay destination: `/replay-share/$shareId`.
 *
 * The same viewer as the hunt route, minus chat: a share never carries one, and the
 * chat endpoint is limited to the hunt's participants. Close goes home, because a
 * recipient arrives from a link and has no page of ours to return to.
 */
export function SharedReplayRoute() {
  const { t } = useTranslation('replay')
  const navigate = useNavigate()
  const { shareId } = useParams({ strict: false }) as { shareId: string }

  const { data, isLoading, error, refetch } = useSharedHuntReplay(shareId)

  // Reported once the snapshot has loaded, and with no id at all: a share's
  // `huntId` is a per-share stand-in that joins to nothing (the real one is kept
  // from link holders), and the share id is the link's access key — the URLs RUM
  // records on its own have it replaced by `rumBeforeSend` in `@wehunt/core-web`'s
  // `datadog.ts`.
  const loaded = data !== undefined
  useEffect(() => {
    if (!loaded) return
    datadogRum.addAction('replay_viewed', { source: 'share' })
  }, [loaded])

  const close = () => void navigate({ to: '/' })

  const bundle = useMemo(
    () => (data === undefined ? undefined : replayDtoToBundle(data)),
    [data],
  )

  if (isLoading) {
    return <ReplayStatusScreen title={t('status.loading')} onClose={close} />
  }

  if (error !== null) {
    // Unknown, expired and revoked links are all final, and say the same thing:
    // telling a stranger which it was gains nothing. A 503 (the API cannot serve
    // shares right now) falls through to the retryable unavailable screen.
    const gone = error.status === 404 || error.status === 410
    return (
      <ReplayStatusScreen
        title={gone ? t('status.linkInvalidTitle') : t('status.unavailableTitle')}
        body={gone ? t('status.linkInvalidBody') : t('status.unavailableBody')}
        onClose={close}
        onRetry={gone ? undefined : () => void refetch()}
      />
    )
  }

  if (bundle === undefined) {
    return <ReplayStatusScreen title={t('status.unavailableTitle')} onClose={close} />
  }

  return <ReplayBundleView bundle={bundle} onClose={close} />
}
